import {Graphics, Point, SCALE_MODES, WebGLRenderer} from "pixi.js";
import {BoosterRenderer} from "../../../game/Utils/BoosterRenderer";
import {BoosterNameEnum, BoosterTypeEnum, GameModel} from "../../../game/Models/GameModel";
import {ModelEvents, SoundManager} from "@denishussar/html5-game-engine";
import {SoundConstants} from "../../../game/Constants/Sounds";
import {SoundModel} from "../../../game/Models/SoundModel";
import {GameLogic} from "../GameLogic";
import {GameEvent, GameEventBoosterUse} from "../../../game/Constants/GameEvent";

export class BucketBooster extends BoosterRenderer {
    private x = 0;
    private y = 0;

    constructor(
        private gameModel: GameModel,
        private soundManager: SoundManager,
        private gameLogic: GameLogic,
        private modelEvents: ModelEvents,
        renderer: WebGLRenderer
    ) {
        super(renderer, {
            width: gameModel.saveTexture.width,
            height: gameModel.saveTexture.height,
            scaleMode: SCALE_MODES.NEAREST,
            resolution: 1
        });

        this.createFill(0, 0);

        this.sprite.width = gameModel.saveTexture.width * GameModel.pixelSize;
        this.sprite.height = gameModel.saveTexture.height * GameModel.pixelSize;
    }

    private createFill(x: number, y: number) {
        const zone = new Graphics(true);
        zone.beginFill(0x008800, 0.3);

        const pixels = this.getBoosterFillArea(x, y);
        if (pixels.length) {
            pixels.forEach(point => zone.drawRect(point.x, point.y, 1, 1));
        }

        zone.endFill();
        this.draw(); // clear render target
        this.draw(zone); // draw zone
        zone.destroy();
    }

    movePointer(x: number, y: number) {
        const previousX = this.x;
        const previousY = this.y;

        this.x = x;
        this.y = y;

        if (x !== previousX || y !== previousY) {
            this.createFill(x, y);
        }
    }

    fill(x: number, y: number): void {
        const pixels = this.getBoosterFillArea(x, y);
        if (pixels.length) {
            pixels.forEach(point => this.gameLogic.fillPixelByBooster(point.x, point.y));

            this.gameLogic.decBooster(BoosterTypeEnum.bucket);

            this.modelEvents.dispatch(this.gameModel, this.gameLogic.isFTUE ? GameEvent.tutorialBoosterUse : GameEvent.boosterUse, <GameEventBoosterUse>{
                boosterName: BoosterNameEnum.bucket,
                levelName: this.gameModel.picture.name,
                levelSize: this.gameModel.currentPictureWidth,
            });

            this.soundManager.play(SoundConstants.BOOSTER_BUCKET, false, SoundModel.fx);
        }

        this.gameLogic.disableBoosters();
    }

    private neighbours(x: number, y: number): number[][] {
        return [
            [x + 1, y],
            [x - 1, y],
            [x, y + 1],
            [x, y - 1],
        ];
    }

    private floodFill(visited: number[][], color, start: number[]): number[][] {
        const columns = this.gameModel.currentPictureWidth;
        const rows = this.gameModel.currentPictureHeight;

        const [x, y] = start;

        if (x < 0 || x >= columns) {
            return [];
        }
        if (y < 0 || y >= rows) {
            return [];
        }

        if (this.gameLogic.getPixelColor(x, y) !== color) {
            return [];
        }

        visited.push(start);

        this.neighbours(x, y).forEach(neighbour => {
            const alreadyVisited = visited.filter(v => JSON.stringify(v) === JSON.stringify(neighbour));
            if (alreadyVisited.length === 0) {
                return this.floodFill(visited, color, neighbour);
            }
        });
    }

    private getBoosterFillArea(x: number, y: number): Point[] {
        const visited = [];

        this.floodFill(visited, this.gameLogic.getColor(), [x, y]);

        return visited.map(point => new Point(point[0], point[1]));
    }
}
